import {AttrsSchema, Component} from 'panel';
import moment from 'moment-timezone';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {currentProjectStore} from 'common/report/stores/project-details';
import {
  getFormattedRowData,
  hasRetentionData,
  mapToSortedUiRows,
  RowWithAggregate,
} from 'common/report/data/retention/parsing';
import {getDataGroupName, isSegmented} from 'common/report/data/retention/format';
import {RetentionQueryResponse} from 'common/report/data/retention/client';
import {processQueryResponse} from 'common/report/data/retention/request-util';
import {FormattedRow, RawCell, RetentionBookmarkParams} from 'common/report/util/retention/types';
import {getFrequencyTooltipSubtitle, getRetentionBucketName, isAddiction} from 'common/report/util/retention/util';
import {IChartDataItem} from 'common/charts/visualization/line-chart/types';
import {TooltipItemType} from 'common/components/chart-tooltip/types';
import {htmlEncodeString} from 'common/util';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DEFAULT_SELECTED_SEGMENTS_LIMIT, MAX_SELECTED_SEGMENTS_LIMIT, ReportName} from 'common/report/constants';

import {percentOrAbsoluteLabel} from '../retention-curve-chart/line-chart-format';
import css from './index.styl?inline';
import {template} from './template';

export type RetentionTrendsChartAttrs = {
  'project-timezone'?: string;
  'utc-offset'?: number;
  'show-annotations'?: boolean;
};

interface LineChartRow extends FormattedRow<RawCell> {
  color?: string;
}

type State = {
  chartRows: LineChartRow[];
};

export type RetentionTrendsChartProps = Pick<RetentionTrendsChart, 'bookmarkParams' | 'data'>;
export class RetentionTrendsChart extends Component<State, RetentionTrendsChartAttrs, any, any, ChartsContextRegistry> {
  private _bookmarkParams: RetentionBookmarkParams;
  private _data: RetentionQueryResponse;

  static get attrsSchema(): AttrsSchema<RetentionTrendsChartAttrs> {
    return {
      'project-timezone': `string`,
      'utc-offset': `number`,
      'show-annotations': `boolean`,
    };
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      contexts: [`segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController({
          defaultNumSelections: DEFAULT_SELECTED_SEGMENTS_LIMIT[ReportName.Retention],
          selectAllLimit: MAX_SELECTED_SEGMENTS_LIMIT[ReportName.Retention],
        }),
      },
      defaultState: {
        chartRows: [],
      },
      helpers: {
        hasResults: () => hasRetentionData(this.getRowAggData()),

        formatTrendsData: (): IChartDataItem[] => {
          return this.state.chartRows
            .filter((row) => this.segmentFilterController.isItemSelected(0, row.raw_head))
            .map((segmentRow) => {
              return {
                name: segmentRow.head as string,
                data: segmentRow.data.map((d) => [
                  d.date.valueOf(),
                  this._bookmarkParams.percentage_view ? d.percent : d.value,
                ]),
                color: segmentRow.color,
                incompleteLength:
                  segmentRow.data.length - (segmentRow.data.findIndex((c) => c.incomplete) || segmentRow.data.length),
              };
            });
        },

        isEndDateIncomplete: () => {
          const row = this.getVisibleRow(0);

          if (!row) return false;
          if (row.data.length < 2) return false;

          return row.data[row.data.length - 1].incomplete;
        },

        getProjectTimezone: () => this.attr(`project-timezone`) || currentProjectStore.value?.timezone,

        getUtcOffset: () => this.attr(`utc-offset`) || currentProjectStore.value?.utcOffset,

        getTimeUnit: () => this._bookmarkParams.date_unit,

        getLineChartProps: () => {
          return {
            chartData: this.helpers.formatTrendsData(),
            formatYAxisLabel: percentOrAbsoluteLabel(this._bookmarkParams.percentage_view),
            formatTooltip: this.helpers.getTrendsChartTooltip(),
          };
        },

        getTrendsChartTooltip: () => {
          return function (this: RetentionTrendsChart, x: number, _y: number, _series: any, idx: number) {
            const {bookmarkParams} = this;
            const row = this.getVisibleRow(idx);
            const cell = row.data.find((cell) => cell.date.valueOf() === x);

            if (!cell) {
              // this case occurs when the retention bucket is changed:
              // there is an out-of-sync state that causes the cell lookup to fail
              return null;
            }

            const dataGroupName = getDataGroupName(bookmarkParams.data_group_id);
            const sections = [
              {
                rows: [
                  ...getHeaderRows(),
                  {
                    items: [
                      {text: `Users entering ${bookmarkParams.date_unit} of`, type: TooltipItemType.Text},
                      {text: moment(x).format(`MMM D`), type: TooltipItemType.Text},
                    ],
                  },
                ],
              },
              {
                rows: [
                  {
                    items: [
                      {text: cell.percent_value, type: TooltipItemType.Data},
                      {
                        text: this._bookmarkParams.retention_type === `addiction` ? `engaged` : `retention`,
                        type: TooltipItemType.Text,
                      },
                    ],
                  },
                  {
                    items: [
                      {text: cell.absolute_value, type: TooltipItemType.Data},
                      {
                        text: `${dataGroupName}s`,
                        type: TooltipItemType.Text,
                      },
                    ],
                  },
                ],
              },
            ];

            const footerText = cell.incomplete ? `footer-text="Incomplete ${bookmarkParams.date_unit}" ` : ``;

            return `<mp-chart-tooltip title-color=${row.color} ${footerText} sections="${htmlEncodeString(
              JSON.stringify(sections),
            )}">`;

            function getHeaderRows() {
              const subtitle = isAddiction(bookmarkParams)
                ? getFrequencyTooltipSubtitle({
                    cumulative: bookmarkParams.cumulative,
                    dateUnit: bookmarkParams.date_unit as TimeUnit,
                    bucketIndex: bookmarkParams.retention_bucket,
                  })
                : getRetentionBucketName({
                    bucketIndex: bookmarkParams.retention_bucket,
                    bookmarkParams,
                  });

              if (isSegmented(bookmarkParams.group_by)) {
                return [
                  {
                    items: [
                      {
                        text: row.head,
                        type: TooltipItemType.Strong,
                      },
                    ],
                  },
                  {
                    items: [{text: subtitle, type: TooltipItemType.Text}],
                  },
                ];
              } else {
                return [
                  {
                    items: [{text: subtitle, type: TooltipItemType.Strong}],
                  },
                ];
              }
            }
          }.bind(this);
        },
      },
    };
  }

  getVisibleRow(idx: number) {
    let i = 0;
    let visibleIdx = 0;
    while (i < this.state.chartRows.length) {
      const row = this.state.chartRows[i];
      if (this.isRowSelected(row)) {
        if (visibleIdx === idx) {
          return row;
        } else {
          visibleIdx++;
        }
      }
      i++;
    }

    return null;
  }

  updateChartRows() {
    const rows = this.getRowAggData();

    if (!rows || !hasRetentionData(rows)) {
      return;
    }

    const formattedRows = mapToSortedUiRows(rows, this.helpers.getProjectTimezone(), this._bookmarkParams);

    this.update({
      chartRows: formattedRows.map((row) => ({
        ...row,
        color: row.selection_color,
      })),
    });
  }

  loadSegmentFilterController() {
    const segmentFilterItems = this.state.chartRows.map((row) => ({
      label: row.raw_head,
      id: row.raw_head,
      color: row.color,
    }));

    const selectedSegmentFilterItems = this._bookmarkParams.selected_segments;

    //can't call panel context if component is not connected to DOM
    if (this.isConnected) {
      this.segmentFilterController.loadSections([{items: segmentFilterItems}]);
      this.segmentFilterController.loadPersistedSelections([selectedSegmentFilterItems]);
    }
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  connectedCallback() {
    super.connectedCallback();

    //TODO add store fetch
    this.loadSegmentFilterController();
  }

  isRowSelected(row: LineChartRow) {
    return this.segmentFilterController.isItemSelected(0, row.raw_head);
  }
  getPercentSelected(rows: LineChartRow[]) {
    let visible = 0;
    for (const row of rows) {
      if (this.isRowSelected(row)) {
        visible++;
      }
    }

    return visible / rows.length;
  }

  getRowAggData(): RowWithAggregate[] {
    if (!this._data || !this._bookmarkParams) {
      return null;
    }
    const {metadata, data} = processQueryResponse(this._data, this._bookmarkParams.group_by);
    return getFormattedRowData(data, metadata, this._bookmarkParams.group_by, this.helpers.getProjectTimezone());
  }

  set bookmarkParams(bookmarkParams) {
    this._bookmarkParams = bookmarkParams;
    this.updateChartRows();
  }

  get bookmarkParams() {
    return this._bookmarkParams;
  }

  set data(data) {
    this._data = data;
    this.updateChartRows();
  }

  get data() {
    return this._data;
  }
}

export const RetentionTrendsChartEl = defineAndMakeJsxFactory<{
  props: RetentionTrendsChartProps;
  attrs?: RetentionTrendsChartAttrs;
}>(`mp-retention-trends-chart`, RetentionTrendsChart);
